import React from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const chartComponentMap = {
  line: Line,
  bar: Bar,
};

const Graph = ({
  labels = [],
  data = [],
  datasets,
  title = "",
  datasetOptions = {},
  options = {},
  chartType = "line",
  valueFormatter,
}) => {
  const ChartComponent = chartComponentMap[chartType] || Line;

  const baseDatasetOptions =
    chartType === "bar"
      ? {
          backgroundColor: "rgba(75,192,192,0.6)",
          borderColor: "rgba(75,192,192,1)",
          borderWidth: 1,
        }
      : {
          borderColor: "rgba(75,192,192,1)",
          backgroundColor: "rgba(75,192,192,0.2)",
          tension: 0.35,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
        };

  const resolvedDatasets = (
    Array.isArray(datasets) && datasets.length
      ? datasets
      : [
          {
            label: title,
            data,
          },
        ]
  ).map((dataset) => ({
    label: dataset.label ?? title,
    data: dataset.data ?? data,
    ...baseDatasetOptions,
    ...datasetOptions,
    ...dataset,
  }));

  const chartData = {
    labels,
    datasets: resolvedDatasets,
  };

  const formatValue = (value) => {
    if (typeof valueFormatter === "function") {
      return valueFormatter(value);
    }
    if (Number.isFinite(value)) {
      return value;
    }
    return value || "";
  };

  const enrichedOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) => {
            const label = context.dataset?.label || "";
            const formattedValue = formatValue(
              context.parsed?.y ?? context.parsed,
            );
            return `${label}: ${formattedValue}`;
          },
        },
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: false,
        ticks: {
          callback: (value) => formatValue(value),
        },
      },
      x: {
        ticks: {
          autoSkip: true,
          maxTicksLimit: 12,
        },
      },
    },
    ...options,
  };

  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
        backgroundColor: "#fff",
      }}
      data-chart-container="true"
    >
      <ChartComponent data={chartData} options={enrichedOptions} />
    </div>
  );
};

export default Graph;
